import { SSO_CLIENT_SECRET_KEY } from '../identity/sso';
import { PLAID_SECRET_KEY } from './bankFeeds';

/**
 * Maps (piece 2's GeoProvider seam, chosen in Settings; decision 0319): the
 * organization picks the map service behind geocoding and travel time, and a
 * Google key is entered once in Settings and held sealed. 🔨 A sealed value is
 * never read back: the screen sees that one is held and its last characters,
 * and nothing else.
 */
import { OCR_GOOGLE_KEY_KEY } from './ocr';
import { STRIPE_SECRET_KEY_KEY, STRIPE_WEBHOOK_SECRET_KEY } from './payments';
import { ZOHO_BOOKS_CLIENT_SECRET_KEY, ZOHO_BOOKS_REFRESH_TOKEN_KEY } from '../import/zohoBooksSettings';
import { ZOHO_CRM_REFRESH_TOKEN_KEY } from '../import/zohoCrm';
import { ZOHO_EXPENSE_REFRESH_TOKEN_KEY } from '../import/zohoExpense';
import { AI_ANTHROPIC_KEY_KEY } from '../ai/aiSeam';
import { PEOPLE_DATA_KEY_KEY } from '../ai/enrichmentSeam';
import { TWILIO_API_KEY_SECRET_KEY, TWILIO_AUTH_TOKEN_KEY } from './twilio';

export const MAPS_PROVIDER_KEY = 'maps.provider';
export const MAPS_GOOGLE_KEY_KEY = 'maps.googleApiKey';
/**
 * 🔒 The Postal Service's Addresses API credentials, a consumer key and secret
 * from the USPS developer portal (decision 3120). Both are sealed, and with both
 * held a United States address asks the Postal Service first.
 */
export const MAPS_USPS_CONSUMER_KEY_KEY = 'maps.uspsConsumerKey';
export const MAPS_USPS_CONSUMER_SECRET_KEY = 'maps.uspsConsumerSecret';
/**
 * 🔒 Geoapify's API key (decision 7320), sealed like the Google key, and the day's limit on its lookups: an Organization
 * setting, a whole number from 0, read as 3000 while no row holds one, where 0 means no cap.
 */
export const MAPS_GEOAPIFY_KEY_KEY = 'maps.geoapifyApiKey';
export const MAPS_GEOAPIFY_DAILY_LIMIT_KEY = 'maps.geoapifyDailyLimit';
/** Geoapify's free plan allows 3,000 requests a day. */
export const DEFAULT_GEOAPIFY_DAILY_LIMIT = 3000;

/** The limit a held setting names, or the default where the row is absent or not a whole number from 0. */
export function geoapifyDailyLimit(held: unknown): number {
  const count = typeof held === 'number' ? held : typeof held === 'string' && held.trim() !== '' ? Number(held) : Number.NaN;
  return Number.isInteger(count) && count >= 0 ? count : DEFAULT_GEOAPIFY_DAILY_LIMIT;
}

/** The refusal a typed daily limit earns, or null where it is a whole number from 0. */
export function geoapifyDailyLimitProblem(value: unknown): string | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? null : 'the Geoapify daily limit is a whole number from 0, and 0 means no limit';
}

/** One United States address service as Settings › Integrations › Maps lists it (decision 7320). */
export interface AddressServiceRow {
  providerKey: string;
  rank: number;
  isActive: boolean;
  /** Whether the service needs a key at all, and whether it is held: the list reads Key held, No key: not asked or Needs no key. */
  key: 'held' | 'missing' | 'none';
  /** Geoapify's lookups made today, and the limit in force (0 for none); null on every other service. */
  usedToday: number | null;
  dailyLimit: number | null;
}

export interface AddressServicesAnswer {
  countryCode: string;
  services: AddressServiceRow[];
}

/** What the switch posts: one binding's provider key and whether it is asked. */
export interface AddressServiceSwitch {
  providerKey: string;
  isActive: boolean;
}

export const MAPS_PROVIDERS = ['Off', 'OpenStreetMap', 'GoogleMaps', 'Geoapify'] as const;
export type MapsProvider = (typeof MAPS_PROVIDERS)[number];
/** OpenStreetMap needs no key, so a fresh installation computes real travel time before anyone configures anything. */
export const DEFAULT_MAPS_PROVIDER: MapsProvider = 'OpenStreetMap';

export function asMapsProvider(value: unknown): MapsProvider {
  return (MAPS_PROVIDERS as readonly unknown[]).includes(value) ? (value as MapsProvider) : DEFAULT_MAPS_PROVIDER;
}

/**
 * The Settings values held sealed in the KeyProvider envelope rather than in
 * the clear (decisions 0319, 0347); the single sign-on client secret joins the
 * two Google keys (decision 0352) and the two payment-processing secrets join
 * them (decision 0465).
 *
 * 🔨 The Plaid secret joins them (decision 0498). It shipped outside this list
 * on 2026-09-17, so the one credential that authenticates every Plaid call was
 * written to its `SettingValue` row in the clear while the Stripe secret beside
 * it was sealed.
 *
 * 🔒 The Zoho Books client secret and refresh token join them (decision 0922).
 *
 * 🔒 The Postal Service's consumer key and secret join them (decision 3120).
 *
 * 🔒 Twilio's auth token joins them (decision 5820).
 *
 * 🔒 Geoapify's API key joins them (decision 7320).
 */
export const SEALED_SETTING_KEYS: readonly string[] = [MAPS_GOOGLE_KEY_KEY, MAPS_USPS_CONSUMER_KEY_KEY, MAPS_USPS_CONSUMER_SECRET_KEY, MAPS_GEOAPIFY_KEY_KEY, OCR_GOOGLE_KEY_KEY, SSO_CLIENT_SECRET_KEY, STRIPE_SECRET_KEY_KEY, STRIPE_WEBHOOK_SECRET_KEY, PLAID_SECRET_KEY, ZOHO_BOOKS_CLIENT_SECRET_KEY, ZOHO_BOOKS_REFRESH_TOKEN_KEY, ZOHO_CRM_REFRESH_TOKEN_KEY, ZOHO_EXPENSE_REFRESH_TOKEN_KEY, AI_ANTHROPIC_KEY_KEY, PEOPLE_DATA_KEY_KEY, TWILIO_AUTH_TOKEN_KEY, TWILIO_API_KEY_SECRET_KEY];

/** What a sealed value reads back as. */
export interface SealedValueView {
  sealed: true;
  /** The last characters of the plaintext, enough to tell two keys apart and never enough to use one. */
  tail: string;
}

export function isSealedValueView(value: unknown): value is SealedValueView {
  return typeof value === 'object' && value !== null && (value as SealedValueView).sealed === true && typeof (value as SealedValueView).tail === 'string';
}

/** The last four characters of a value at least twelve long; a shorter value shows nothing. */
export function sealedTail(plaintext: string): string {
  return plaintext.length >= 12 ? plaintext.slice(-4) : '';
}
